// SCR-STAFF-417 — میز آفلاین پذیرش و صندوق (ADR-011، ARD-offline؛ AP-OFF-01 — C-AGENT-1550): برگه روز از Snapshot رمزشده (امروز شعبه فعال)،
// CheckIn، ثبت سریع (FLOW-01)، صدور و دریافت نقد یا کارتخوان مستقل (FLOW-05) با رسید شماره موقت OFF- و نشان «آفلاین»، فهرست Outbox با نتیجه
// همگام‌سازی (شماره قطعی، تعارض و اقدام پیشنهادی). همه عملیات رمزشده در Outbox دستگاه می‌نشینند و پس از اتصال به ترتیب همگام می‌شوند.
// استرداد، تغییر تعرفه، درگاه و کیف پول و پرونده بالینی آفلاین در دسترس نیستند (ARD-offline §۱).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type {
  BilOfflineAppointmentView,
  BilOfflineServiceView,
  BilOfflineSnapshotView,
  BilPaymentMethod,
} from '@phoenix/api-client';
import { useAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, EmptyState, ErrorState, FormDialog, JalaliDate, LoadingState, Money, PageHeader, faDigits, latinDigits, type DataTableColumn } from '@phoenix/ui';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router';
import { SYNC_CONFLICTS_PATH } from '../../offline/ConnectionIndicator';
import { useOffline } from '../../offline/OfflineProvider';
import type { OutboxEntry } from '../../offline/vault';
import { formatTime } from '../scheduling/common';

export const SCR_STAFF_417 = 'SCR-STAFF-417';

type DeskTab = 'day' | 'register' | 'outbox';

/** مراجعه‌کننده صندوق آفلاین: پرونده موجود یا ثبت آفلاین همین دستگاه. */
interface CheckoutTarget {
  patientId?: string;
  patientOperationId?: string;
  name: string;
  appointment?: BilOfflineAppointmentView;
}

interface DraftLine {
  serviceId: string;
  quantity: number;
  unitPrice: number;
  appointmentItemId?: string;
}

const OFFLINE_METHODS: readonly BilPaymentMethod[] = ['Cash', 'CardPos'];
const CHECKED_IN = new Set(['CheckedIn', 'InProgress', 'Completed']);

function serviceName(t: (key: string) => string, service: BilOfflineServiceView | undefined): string {
  return service?.name ?? t('bil:offline.masked_service');
}

function useSnapshotLookups(snapshot: BilOfflineSnapshotView | undefined) {
  return useMemo(
    () => ({
      services: new Map((snapshot?.services ?? []).map((service) => [service.id, service])),
      patients: new Map((snapshot?.patients ?? []).map((patient) => [patient.id, patient])),
    }),
    [snapshot],
  );
}

export function OfflineDeskPage() {
  const { t } = useTranslation('bil');
  const offline = useOffline();
  const access = useAccess();
  const [tab, setTab] = useState<DeskTab>('day');
  const [target, setTarget] = useState<CheckoutTarget | null>(null);
  const [receipt, setReceipt] = useState<OutboxEntry | null>(null);
  const baseId = useId();
  const snapshot = offline.snapshot?.snapshot;

  const header = (
    <PageHeader
      titleKey="bil:offline.title"
      subtitleKey="bil:offline.subtitle"
      actions={
        <Stack direction="row" spacing={1}>
          {access.can('bil.sync_conflict.manage') ? (
            <Button component={RouterLink} to={SYNC_CONFLICTS_PATH} data-testid="offline-open-conflicts">
              {t('bil:offline.review_queue')}
            </Button>
          ) : null}
          <Button onClick={() => void offline.refreshSnapshot()} disabled={offline.status === 'offline'} data-testid="offline-refresh-snapshot">
            {t('bil:offline.refresh_snapshot')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void offline.syncNow()}
            disabled={offline.status === 'offline' || offline.syncing || offline.pendingCount === 0}
            data-testid="offline-sync-now"
          >
            {t('bil:offline.sync_now', { count: offline.pendingCount })}
          </Button>
        </Stack>
      }
    />
  );

  if (!offline.ready) {
    return (
      <section data-screen-id={SCR_STAFF_417}>
        {header}
        {offline.error ? <ErrorState error={offline.error} /> : <LoadingState rows={4} />}
      </section>
    );
  }

  return (
    <section data-screen-id={SCR_STAFF_417}>
      {header}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Alert
          severity={offline.status === 'offline' ? 'warning' : 'success'}
          data-testid="offline-desk-status"
          data-state={offline.status}
        >
          {offline.status === 'offline' ? t('bil:offline.status.offline', { count: offline.pendingCount }) : t('bil:offline.status.online')}
        </Alert>
        {offline.sessionExpired ? (
          <Alert severity="error" data-testid="offline-session-expired">
            {t('bil:offline.session_expired')}
          </Alert>
        ) : null}
        {offline.lastSync && offline.lastSync.sent > 0 ? (
          <Alert severity={offline.lastSync.conflicts > 0 ? 'warning' : 'info'} data-testid="offline-last-sync">
            {t('bil:offline.last_sync', { applied: offline.lastSync.applied, conflicts: offline.lastSync.conflicts })}
          </Alert>
        ) : null}
        {offline.snapshot ? (
          <Typography variant="body2" color="text.secondary" data-testid="offline-snapshot-info">
            {t('bil:offline.snapshot_info')} <JalaliDate value={snapshot?.generatedAt} withTime /> · {t('bil:offline.snapshot_expires')}{' '}
            <JalaliDate value={snapshot?.expiresAt} withTime /> · {t('bil:offline.device', { device: offline.deviceId ?? '' })}
          </Typography>
        ) : null}
      </Stack>
      <Tabs value={tab} onChange={(_, value: DeskTab) => setTab(value)} sx={{ mb: 2 }} aria-label={t('bil:offline.title')}>
        <Tab value="day" label={t('bil:offline.tabs.day')} id={`${baseId}-tab-day`} aria-controls={`${baseId}-panel`} data-testid="offline-tab-day" />
        <Tab value="register" label={t('bil:offline.tabs.register')} id={`${baseId}-tab-register`} aria-controls={`${baseId}-panel`} data-testid="offline-tab-register" />
        <Tab
          value="outbox"
          label={t('bil:offline.tabs.outbox', { count: offline.entries.length })}
          id={`${baseId}-tab-outbox`}
          aria-controls={`${baseId}-panel`}
          data-testid="offline-tab-outbox"
        />
      </Tabs>
      <div role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${tab}`}>
        {!snapshot && tab !== 'outbox' ? (
          <EmptyState titleKey="bil:offline.no_snapshot" descriptionKey="bil:offline.no_snapshot_hint" />
        ) : tab === 'day' && snapshot ? (
          <DaySheet snapshot={snapshot} onCashier={setTarget} />
        ) : tab === 'register' && snapshot ? (
          <QuickRegister onCashier={setTarget} />
        ) : (
          <OutboxList onReceipt={setReceipt} />
        )}
      </div>
      {target && snapshot ? (
        <CheckoutDialog
          target={target}
          snapshot={snapshot}
          onClose={() => setTarget(null)}
          onDone={(entry) => {
            setTarget(null);
            setReceipt(entry);
          }}
        />
      ) : null}
      {receipt && snapshot ? <ReceiptDialog entry={receipt} snapshot={snapshot} onClose={() => setReceipt(null)} /> : null}
    </section>
  );
}

// ───────────────────────────── برگه روز ─────────────────────────────

function DaySheet({ snapshot, onCashier }: { snapshot: BilOfflineSnapshotView; onCashier: (target: CheckoutTarget) => void }) {
  const { t } = useTranslation('bil');
  const offline = useOffline();
  const lookups = useSnapshotLookups(snapshot);
  const local = useMemo(() => {
    const map = new Map<string, OutboxEntry>();
    for (const entry of offline.entries) {
      if (entry.operation.kind === 'CheckIn' && entry.operation.checkIn) {
        map.set(entry.operation.checkIn.appointmentId, entry);
      }
    }
    return map;
  }, [offline.entries]);
  const rows = snapshot.appointments.filter((appointment) => appointment.businessDate === snapshot.businessDate);
  const canCheckIn = snapshot.capabilities.checkIn && !offline.sessionExpired;
  const canCheckout = snapshot.capabilities.checkout && !offline.sessionExpired;

  const columns: DataTableColumn<BilOfflineAppointmentView>[] = [
    { id: 'time', headerKey: 'bil:offline.columns.time', cell: (row) => formatTime(row.start, snapshot.timeZone) },
    {
      id: 'patient',
      headerKey: 'bil:offline.columns.patient',
      cell: (row) => {
        const patient = lookups.patients.get(row.patientId);
        return patient ? `${patient.displayName} · ${faDigits(patient.fileNumber)}` : '—';
      },
    },
    {
      id: 'service',
      headerKey: 'bil:offline.columns.service',
      cell: (row) => row.items.map((item) => serviceName(t, lookups.services.get(item.serviceId))).join(t('bil:offline.list_separator')),
    },
    { id: 'provider', headerKey: 'bil:offline.columns.provider', cell: (row) => row.items[0]?.providerName ?? '—' },
    {
      id: 'status',
      headerKey: 'bil:offline.columns.status',
      cell: (row) => {
        const entry = local.get(row.id);
        if (entry) {
          return (
            <Chip
              size="small"
              color={entry.status === 'conflict' ? 'error' : entry.status === 'pending' ? 'warning' : 'success'}
              label={t(`bil:offline.local_checkin.${entry.status}`)}
              data-testid="offline-local-checkin"
            />
          );
        }
        return t(`sch:status.${row.status}`);
      },
    },
    {
      id: 'actions',
      headerKey: 'bil:offline.columns.actions',
      cell: (row) => {
        const checkedIn = CHECKED_IN.has(row.status) || (local.get(row.id) !== undefined && local.get(row.id)?.status !== 'conflict');
        const bookable = row.status === 'Booked' || row.status === 'Confirmed';
        const patient = lookups.patients.get(row.patientId);
        return (
          <Stack direction="row" spacing={1}>
            {bookable && !local.has(row.id) ? (
              <Button
                size="small"
                variant="contained"
                disabled={!canCheckIn}
                onClick={() => void offline.checkIn(row.id, { patientName: patient?.displayName })}
                data-testid="offline-checkin"
              >
                {t('bil:offline.check_in')}
              </Button>
            ) : null}
            {checkedIn ? (
              <Button
                size="small"
                disabled={!canCheckout}
                onClick={() => onCashier({ patientId: row.patientId, name: patient?.displayName ?? '—', appointment: row })}
                data-testid="offline-cashier"
              >
                {t('bil:offline.cashier')}
              </Button>
            ) : null}
          </Stack>
        );
      },
    },
  ];

  return (
    <DataTable
      captionKey="bil:offline.tabs.day"
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      isLoading={false}
      error={null}
      emptyTitleKey="bil:offline.no_appointments"
    />
  );
}

// ───────────────────────────── ثبت سریع (FLOW-01) ─────────────────────────────

function QuickRegister({ onCashier }: { onCashier: (target: CheckoutTarget) => void }) {
  const { t } = useTranslation('bil');
  const offline = useOffline();
  const snapshot = offline.snapshot?.snapshot;
  const [form, setForm] = useState({ mobile: '', firstName: '', lastName: '', gender: 'Female' as 'Female' | 'Male', nationalId: '' });
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const mobile = latinDigits(form.mobile).replace(/\s/g, '');
  const valid = /^09\d{9}$/.test(mobile) && form.firstName.trim() !== '' && form.lastName.trim() !== '';
  const registered = offline.entries.filter((entry) => entry.operation.kind === 'PatientRegister');
  const allowed = (snapshot?.capabilities.register ?? false) && !offline.sessionExpired;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || !allowed) {
      setFailed(true);
      return;
    }
    const name = `${form.firstName.trim()} ${form.lastName.trim()}`;
    await offline.register(
      {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        gender: form.gender,
        mobile,
        nationalId: latinDigits(form.nationalId).trim() || null,
        birthDate: null,
      },
      { patientName: name },
    );
    setForm({ mobile: '', firstName: '', lastName: '', gender: 'Female', nationalId: '' });
    setFailed(false);
    setSaved(true);
  };

  return (
    <Stack spacing={2}>
      <Paper component="form" noValidate onSubmit={(event: FormEvent) => void submit(event)} sx={{ p: 2 }} data-testid="offline-register-form">
        <Stack spacing={2}>
          {saved ? <Alert severity="success">{t('bil:offline.register_saved')}</Alert> : null}
          {failed ? <Alert severity="error">{t('bil:offline.register_invalid')}</Alert> : null}
          <TextField
            label={t('bil:offline.fields.mobile')}
            value={form.mobile}
            required
            autoFocus
            onChange={(event) => setForm({ ...form, mobile: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel', 'data-testid': 'offline-register-mobile' } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              required
              label={t('bil:offline.fields.first_name')}
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'offline-register-first' } }}
            />
            <TextField
              fullWidth
              required
              label={t('bil:offline.fields.last_name')}
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'offline-register-last' } }}
            />
          </Stack>
          <RadioGroup row value={form.gender} onChange={(event) => setForm({ ...form, gender: event.target.value as 'Female' | 'Male' })} aria-label={t('bil:offline.fields.gender')}>
            <FormControlLabel value="Female" control={<Radio />} label={t('bil:offline.fields.female')} />
            <FormControlLabel value="Male" control={<Radio />} label={t('bil:offline.fields.male')} />
          </RadioGroup>
          <TextField
            label={t('bil:offline.fields.national_id')}
            value={form.nationalId}
            onChange={(event) => setForm({ ...form, nationalId: event.target.value })}
            helperText={t('bil:offline.fields.national_id_hint')}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'offline-register-national' } }}
          />
          <Stack direction="row">
            <Button type="submit" variant="contained" disabled={!allowed} data-testid="offline-register-submit">
              {t('bil:offline.register')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {registered.length > 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {t('bil:offline.registered_title')}
          </Typography>
          <Stack spacing={1}>
            {registered.map((entry) => (
              <Stack key={entry.operation.clientOperationId} direction="row" spacing={2} alignItems="center" data-testid="offline-registered-row">
                <Typography sx={{ flex: 1 }}>{entry.display?.patientName ?? '—'}</Typography>
                <Chip size="small" label={t(`bil:offline.entry_status.${entry.status}`)} />
                <Button
                  size="small"
                  disabled={!(snapshot?.capabilities.checkout ?? false) || offline.sessionExpired}
                  onClick={() =>
                    onCashier({
                      patientOperationId: entry.status === 'pending' ? entry.operation.clientOperationId : undefined,
                      patientId: entry.status === 'pending' ? undefined : (entry.result?.outcome?.patientId ?? undefined),
                      name: entry.display?.patientName ?? '—',
                    })
                  }
                  data-testid="offline-registered-cashier"
                >
                  {t('bil:offline.cashier')}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

// ───────────────────────────── صندوق آفلاین (FLOW-05) ─────────────────────────────

function CheckoutDialog({
  target,
  snapshot,
  onClose,
  onDone,
}: {
  target: CheckoutTarget;
  snapshot: BilOfflineSnapshotView;
  onClose: () => void;
  onDone: (entry: OutboxEntry) => void;
}) {
  const { t } = useTranslation('bil');
  const offline = useOffline();
  const lookups = useSnapshotLookups(snapshot);
  const priced = snapshot.services.filter((service) => service.unitPrice && !service.requiresAmount);
  const [lines, setLines] = useState<DraftLine[]>(() =>
    (target.appointment?.items ?? []).map((item) => ({
      serviceId: item.serviceId,
      quantity: Number(item.quantity),
      unitPrice: Number(lookups.services.get(item.serviceId)?.unitPrice?.amount ?? 0),
      appointmentItemId: item.itemId,
    })),
  );
  const total = lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
  const [method, setMethod] = useState<BilPaymentMethod>('Cash');
  const [amountText, setAmountText] = useState('');
  const [reference, setReference] = useState('');
  const [registerId, setRegisterId] = useState(snapshot.registers[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const amount = amountText === '' ? total : Number(latinDigits(amountText).replace(/[^0-9]/g, '') || '0');
  const unpriced = lines.some((line) => !lookups.services.get(line.serviceId)?.unitPrice);
  const valid = lines.length > 0 && !unpriced && amount > 0 && (method !== 'CardPos' || reference.trim() !== '');

  const submit = async () => {
    if (!valid) {
      setError(new Error('invalid'));
      return;
    }
    setBusy(true);
    try {
      const entry = await offline.checkout(
        {
          patientId: target.patientId ?? null,
          patientOperationId: target.patientOperationId ?? null,
          appointmentId: target.appointment?.id ?? null,
          lines: lines.map((line) => ({
            serviceId: line.serviceId,
            quantity: line.quantity,
            unitPrice: { amount: line.unitPrice, currency: 'IRR' },
            appointmentItemId: line.appointmentItemId ?? null,
          })),
          payments: [
            {
              method,
              amount: { amount, currency: 'IRR' },
              reference: method === 'CardPos' ? reference.trim() : null,
              cardLast4: null,
            },
          ],
          registerId: registerId || null,
        },
        { patientName: target.name, amount },
      );
      onDone(entry);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog
      open
      titleKey="bil:offline.checkout_title"
      screenId={SCR_STAFF_417}
      onSubmit={() => void submit()}
      onClose={onClose}
      isDirty={lines.length > (target.appointment?.items.length ?? 0) || amountText !== '' || reference !== ''}
      isSubmitting={busy}
      submitError={error instanceof Error && error.message === 'invalid' ? undefined : error}
      submitKey="bil:offline.issue_and_receive"
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ mt: 1 }} data-testid="offline-checkout-dialog">
        <Alert severity="info">{t('bil:offline.checkout_hint', { name: target.name })}</Alert>
        {lines.map((line, index) => (
          <Stack key={`${line.serviceId}-${index}`} direction="row" spacing={2} alignItems="center" data-testid="offline-checkout-line">
            <Typography sx={{ flex: 1 }}>{serviceName(t, lookups.services.get(line.serviceId))}</Typography>
            <Typography>× {faDigits(line.quantity)}</Typography>
            <Money amountMinor={line.unitPrice * line.quantity} />
            {line.appointmentItemId ? null : (
              <Button size="small" color="error" onClick={() => setLines(lines.filter((_, at) => at !== index))}>
                {t('bil:offline.remove_line')}
              </Button>
            )}
          </Stack>
        ))}
        <TextField
          select
          label={t('bil:offline.add_service')}
          value=""
          onChange={(event) => {
            const service = lookups.services.get(event.target.value);
            if (service?.unitPrice) {
              setLines([...lines, { serviceId: service.id, quantity: 1, unitPrice: Number(service.unitPrice.amount) }]);
            }
          }}
          slotProps={{ htmlInput: { 'data-testid': 'offline-checkout-add-service' } }}
        >
          {priced.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {serviceName(t, service)}
            </MenuItem>
          ))}
        </TextField>
        {unpriced ? <Alert severity="warning">{t('bil:offline.unpriced')}</Alert> : null}
        <Typography variant="h3" data-testid="offline-checkout-total" data-amount={total}>
          {t('bil:offline.total')}: <Money amountMinor={total} />
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            fullWidth
            label={t('bil:offline.method')}
            value={method}
            onChange={(event) => setMethod(event.target.value as BilPaymentMethod)}
            slotProps={{ htmlInput: { 'data-testid': 'offline-checkout-method' } }}
          >
            {OFFLINE_METHODS.filter((value) => snapshot.methods.includes(value)).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:method.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label={t('bil:offline.amount')}
            value={amountText === '' ? String(total) : amountText}
            onChange={(event) => setAmountText(event.target.value)}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'offline-checkout-amount' } }}
          />
        </Stack>
        {method === 'CardPos' ? (
          <TextField
            required
            label={t('bil:offline.reference')}
            helperText={t('bil:offline.reference_hint')}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'offline-checkout-reference' } }}
          />
        ) : null}
        {snapshot.registers.length > 0 ? (
          <TextField
            select
            label={t('bil:offline.register_field')}
            value={registerId}
            onChange={(event) => setRegisterId(event.target.value)}
            helperText={snapshot.openSessionId ? undefined : t('bil:offline.offline_shift_hint')}
            slotProps={{ htmlInput: { 'data-testid': 'offline-checkout-register' } }}
          >
            {snapshot.registers.map((register) => (
              <MenuItem key={register.id} value={register.id}>
                {register.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {error instanceof Error && error.message === 'invalid' ? <Alert severity="error">{t('bil:offline.checkout_invalid')}</Alert> : null}
      </Stack>
    </FormDialog>
  );
}

// ───────────────────────────── رسید آفلاین ─────────────────────────────

function ReceiptDialog({ entry, snapshot, onClose }: { entry: OutboxEntry; snapshot: BilOfflineSnapshotView; onClose: () => void }) {
  const { t } = useTranslation('bil');
  const titleId = useId();
  const lookups = useSnapshotLookups(snapshot);
  const checkout = entry.operation.checkout;
  const finalNumber = entry.result?.outcome?.receiptNumber;
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>{t('bil:offline.receipt_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} data-testid="offline-receipt">
          <Chip color="warning" label={t('bil:offline.receipt_badge')} sx={{ alignSelf: 'flex-start' }} />
          <Typography>
            {t('bil:offline.receipt_number')}:{' '}
            <span dir="ltr" data-testid="offline-receipt-number">
              {entry.display?.provisionalNumber ?? '—'}
            </span>
          </Typography>
          {finalNumber ? (
            <Typography data-testid="offline-receipt-final">
              {t('bil:offline.final_number')}: <span dir="ltr">{finalNumber}</span>
            </Typography>
          ) : null}
          <Typography>{entry.display?.patientName ?? '—'}</Typography>
          <JalaliDate value={entry.operation.occurredAt} withTime />
          {(checkout?.lines ?? []).map((line, index) => (
            <Stack key={`${line.serviceId}-${index}`} direction="row" justifyContent="space-between">
              <span>{serviceName(t, lookups.services.get(line.serviceId))}</span>
              <Money amountMinor={Number(line.unitPrice.amount) * Number(line.quantity)} />
            </Stack>
          ))}
          {(checkout?.payments ?? []).map((payment, index) => (
            <Stack key={index} direction="row" justifyContent="space-between">
              <span>{t(`bil:method.${payment.method}`)}</span>
              <Money amountMinor={Number(payment.amount.amount)} />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => window.print()} data-testid="offline-receipt-print">
          {t('bil:offline.print')}
        </Button>
        <Button variant="contained" onClick={onClose} data-testid="offline-receipt-close">
          {t('bil:offline.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────────── Outbox ─────────────────────────────

function OutboxList({ onReceipt }: { onReceipt: (entry: OutboxEntry) => void }) {
  const { t } = useTranslation('bil');
  const offline = useOffline();
  const rows = [...offline.entries].reverse();
  const columns: DataTableColumn<OutboxEntry>[] = [
    { id: 'time', headerKey: 'bil:offline.columns.occurred', cell: (row) => <JalaliDate value={row.operation.occurredAt} withTime /> },
    { id: 'kind', headerKey: 'bil:offline.columns.kind', cell: (row) => t(`bil:offline.kind.${row.operation.kind}`) },
    { id: 'patient', headerKey: 'bil:offline.columns.patient', cell: (row) => row.display?.patientName ?? '—' },
    {
      id: 'number',
      headerKey: 'bil:offline.columns.number',
      cell: (row) =>
        row.display?.provisionalNumber ? (
          <span dir="ltr" data-testid="offline-outbox-number">
            {row.display.provisionalNumber}
            {row.result?.outcome?.invoices?.[0] ? ` → ${row.result.outcome.invoices[0].number}` : ''}
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'status',
      headerKey: 'bil:offline.columns.status',
      cell: (row) => (
        <Stack spacing={0.5}>
          <Chip
            size="small"
            color={row.status === 'conflict' ? 'error' : row.status === 'pending' ? 'warning' : 'success'}
            label={t(`bil:offline.entry_status.${row.status}`)}
            data-testid="offline-outbox-status"
            data-status={row.status}
            data-kind={row.operation.kind}
          />
          {row.result?.conflictCode ? (
            <Typography variant="caption">
              {t(`bil:offline.conflict.${row.result.conflictCode}`)} — {t(`bil:offline.action.${row.result.suggestedAction ?? 'review'}`)}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'bil:offline.columns.actions',
      cell: (row) =>
        row.operation.kind === 'Checkout' ? (
          <Button size="small" onClick={() => onReceipt(row)} data-testid="offline-outbox-receipt">
            {t('bil:offline.reprint')}
          </Button>
        ) : null,
    },
  ];
  return (
    <DataTable
      captionKey="bil:offline.tabs.outbox"
      columns={columns}
      rows={rows}
      getRowId={(row) => row.operation.clientOperationId}
      isLoading={false}
      error={null}
      emptyTitleKey="bil:offline.outbox_empty"
    />
  );
}
